import React, { useEffect, useState } from 'react';
import { api, formatCOP, imageUrl } from '../services/api';
import { useCart } from '../context/CartContext';
import { useVendor } from '../context/VendorContext';
import BrandLogo from './BrandLogo';
import SucursalPicker from './SucursalPicker';
import OrderConfirmModal from './OrderConfirmModal';
import PedidoOpciones, { getPedidoMetaPayload } from './PedidoOpciones';
import { cerrarVisitaSilent } from '../hooks/useVisitaTracking';
import {
  enqueueOfflinePedido,
  isInvalidVisitError,
  isNetworkError,
  removeOfflinePedido,
  updateOfflinePedido,
} from '../utils/offlinePedidoQueue';
import { getCartSessionId } from '../utils/cartSession';
import { mysqlNow, toMysqlDatetime } from '../utils/mysqlDatetime';

const EDITING_OFFLINE_KEY = 'oh_editing_offline_pedido_id';

/**
 * Observación por línea: estado local al escribir.
 * En WebView Android el input 100% controlado desde el carrito
 * mueve el cursor al inicio y “escribe al revés”.
 */
function CartItemQtyField({ lineId, value, onCommit }) {
  const [draft, setDraft] = useState(String(value || 1));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(String(value || 1));
  }, [value, lineId, focused]);

  function commit(raw) {
    const n = Math.max(0, parseInt(String(raw).replace(/\D/g, ''), 10) || 0);
    if (n < 1) {
      onCommit(0);
      return;
    }
    setDraft(String(n));
    onCommit(n);
  }

  return (
    <div className="cart-item-qty">
      <button type="button" onClick={() => commit(Number(draft) - 1)} aria-label="Menos">
        −
      </button>
      <input
        className="cart-item-qty-input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label="Cantidad"
        value={draft}
        onFocus={() => setFocused(true)}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
        onBlur={() => {
          setFocused(false);
          commit(draft);
        }}
      />
      <button type="button" onClick={() => commit(Number(draft) + 1)} aria-label="Más">
        +
      </button>
      <button
        type="button"
        className="cart-item-borrar"
        onClick={() => onCommit(0)}
        aria-label="Borrar producto"
      >
        Borrar
      </button>
    </div>
  );
}

function CartItemObsField({ lineId, value, onCommit }) {
  const [draft, setDraft] = useState(value || '');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(value || '');
  }, [value, lineId, focused]);

  return (
    <label className="cart-item-obs-label">
      <span>Observación</span>
      <textarea
        className="cart-item-obs"
        rows={2}
        dir="ltr"
        inputMode="text"
        enterKeyHint="done"
        autoCorrect="off"
        autoCapitalize="sentences"
        placeholder="Talla, color, detalle para bodega…"
        value={draft}
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(next);
          onCommit(next);
        }}
        onBlur={() => {
          setFocused(false);
          onCommit(draft);
        }}
      />
    </label>
  );
}

function newRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `pedido-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function takeEditingOfflineId() {
  try {
    return sessionStorage.getItem(EDITING_OFFLINE_KEY) || null;
  } catch {
    return null;
  }
}

function clearEditingOfflineId() {
  try {
    sessionStorage.removeItem(EDITING_OFFLINE_KEY);
  } catch {
    /* ignore */
  }
}

export default function CartPanel({
  onPedidoCreado,
  onPedidoGuardadoOffline,
  onNuevoCliente,
  collapsed = false,
  onToggleCollapse,
}) {
  const { state, dispatch, totalItems, subtotal } = useCart();
  const { vendedor } = useVendor();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  /** Snapshot after save — keeps factura for PDF / WhatsApp after CLEAR. */
  const [successSnap, setSuccessSnap] = useState(null);
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  const { cliente, items } = state;

  function snapshotPedido(pedido = {}, { offline: wasOffline = false, entry = null } = {}) {
    return {
      pedido: pedido || {},
      entry,
      cliente: { ...state.cliente },
      items: state.items.map((i) => ({ ...i })),
      pedidoMeta: { ...(state.pedidoMeta || {}) },
      subtotal,
      offline: wasOffline,
    };
  }

  function finishSuccess() {
    const snap = successSnap;
    setSuccessSnap(null);
    setShowConfirm(false);
    setError('');
    if (snap?.offline) {
      onPedidoGuardadoOffline?.(snap.entry, null, { navigate: true, toast: false });
    } else if (snap?.pedido) {
      onPedidoCreado?.(snap.pedido, { navigate: true, toast: false });
    }
  }

  useEffect(() => {
    if (!vendedor) return;
    dispatch({
      type: 'SET_CLIENTE',
      data: { vendedor: vendedor.nombre, vendedor_id: vendedor.id },
    });
  }, [vendedor, dispatch]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  function buildPayload(fin) {
    const visitaId = cliente.visita_id;
    return {
      client_request_id: newRequestId(),
      cart_session_id: getCartSessionId(),
      cliente_id: cliente.cliente_id || null,
      cliente_nombre: cliente.nombre.trim(),
      cliente_telefono: cliente.telefono.trim() || null,
      notas: cliente.notas.trim() || null,
      vendedor: cliente.vendedor.trim() || null,
      vendedor_id: cliente.vendedor_id || vendedor?.id || null,
      sucursal_id: cliente.sucursal_id || null,
      sucursal_codigo: cliente.sucursal_codigo || null,
      sucursal_direccion: cliente.direccion || null,
      visita_id: visitaId || null,
      visita_inicio: toMysqlDatetime(cliente.visita_inicio) || null,
      visita_fin: toMysqlDatetime(fin) || fin,
      ...getPedidoMetaPayload(state.pedidoMeta),
      items: items.map((i) => ({
        producto_id: i.producto_id,
        codigo: i.codigo != null ? String(i.codigo) : '',
        nombre: i.nombre,
        precio_unitario: i.precio_unitario,
        cantidad: i.cantidad,
        observaciones: i.observaciones || null,
        imagen: i.imagen || null,
        opcion_id: i.opcion_id || null,
      })),
    };
  }

  function guardarOffline(payload, reasonMsg, { holdUntilManual = false } = {}) {
    try {
      const editingId = takeEditingOfflineId();
      let entry;
      if (editingId) {
        const itemsCount = (payload.items || []).reduce(
          (n, i) => n + Number(i.cantidad || 0),
          0
        );
        updateOfflinePedido(editingId, {
          payload,
          cliente_nombre: payload.cliente_nombre || 'Cliente',
          items_count: itemsCount,
          subtotal: Number(subtotal) || 0,
          hold_until_manual: Boolean(holdUntilManual),
          last_error: null,
          attempts: 0,
        });
        entry = {
          id: editingId,
          payload,
          cliente_nombre: payload.cliente_nombre || 'Cliente',
          items_count: itemsCount,
          subtotal: Number(subtotal) || 0,
          hold_until_manual: Boolean(holdUntilManual),
        };
        clearEditingOfflineId();
      } else {
        entry = enqueueOfflinePedido({
          payload,
          subtotal,
          vendedorId: vendedor?.id || null,
          holdUntilManual,
        });
      }
      if (cliente.visita_id && !cliente.visita_fin) {
        cerrarVisitaSilent(cliente.visita_id);
      }
      const snap = snapshotPedido(
        { numero: entry?.id || null, id: entry?.id || null },
        { offline: true, entry }
      );
      setSuccessSnap(snap);
      dispatch({ type: 'CLEAR' });
      setError('');
      onPedidoGuardadoOffline?.(entry, reasonMsg, { navigate: false });
      return snap;
    } catch (err) {
      setError(err.message || 'No se pudo guardar el pedido en la tablet');
      return null;
    }
  }

  async function crearPedidoSafe(payload) {
    try {
      return await api.crearPedido(payload);
    } catch (err) {
      if (isInvalidVisitError(err) && payload.visita_id) {
        return api.crearPedido({ ...payload, visita_id: null });
      }
      throw err;
    }
  }

  /**
   * Guarda en la tablet, vacía el carrito (puedes tomar otro) y NO envía a oficina.
   * Después: banner «por enviar» → Enviar ahora.
   */
  function guardarLocal() {
    setError('');
    if (!cliente.nombre.trim()) {
      setError('Selecciona una sucursal / cliente');
      return;
    }
    if (!items.length) {
      setError('Agrega productos al pedido');
      return;
    }
    const fin = mysqlNow();
    dispatch({ type: 'SET_VISITA_FIN', fin });
    const payload = buildPayload(fin);
    guardarOffline(
      payload,
      'Guardado en tablet. Puedes tomar otro pedido; envíalo a oficina cuando quieras.',
      { holdUntilManual: true }
    );
  }

  /** Enviar a oficina + admin (API). Offline → cola para sincronizar. */
  async function enviarPedidoOficina() {
    setError('');
    if (!cliente.nombre.trim()) {
      setError('Selecciona una sucursal / cliente');
      return null;
    }
    if (!items.length) {
      setError('Agrega productos al pedido');
      return null;
    }

    setSending(true);
    const fin = mysqlNow();
    dispatch({ type: 'SET_VISITA_FIN', fin });
    const payload = buildPayload(fin);
    const visitaId = cliente.visita_id;

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const snap = guardarOffline(payload, 'Sin internet — quedará en cola para oficina');
      setSending(false);
      return snap;
    }

    if (visitaId) {
      cerrarVisitaSilent(visitaId);
    }

    try {
      const res = await crearPedidoSafe(payload);
      const editingId = takeEditingOfflineId();
      if (editingId) {
        removeOfflinePedido(editingId);
        clearEditingOfflineId();
      }
      const snap = snapshotPedido(res.data || {}, { offline: false });
      setSuccessSnap(snap);
      dispatch({ type: 'CLEAR' });
      onPedidoCreado?.(res.data, { navigate: false });
      return snap;
    } catch (err) {
      if (isNetworkError(err)) {
        return guardarOffline(
          payload,
          'Sin conexión — en cola. Se enviará a oficina al volver internet.'
        );
      }
      setError(err.message || 'No se pudo enviar el pedido a oficina');
      return null;
    } finally {
      setSending(false);
    }
  }

  return (
    <aside className={`cart-panel${collapsed ? ' cart-panel--collapsed' : ''}`}>
      {collapsed ? (
        <div className="cart-rail">
          <BrandLogo size="sm" />
          <button
            type="button"
            className="cart-minimize-btn"
            onClick={onToggleCollapse}
            aria-label="Expandir pedido"
            title="Expandir pedido"
          >
            «
          </button>
          <button type="button" className="cart-rail-main" onClick={onToggleCollapse}>
            <span className="cart-rail-count">{totalItems}</span>
            <span className="cart-rail-price">{formatCOP(subtotal)}</span>
            <span className="cart-rail-open">Abrir</span>
          </button>
        </div>
      ) : (
        <>
          <div className="cart-panel-header">
            <div className="cart-panel-brand">
              <BrandLogo size="sm" />
              <div className="cart-panel-title">
                <h2>Pedido</h2>
                <span className="cart-badge">{totalItems}</span>
              </div>
            </div>
            {onToggleCollapse ? (
              <button
                type="button"
                className="cart-minimize-btn"
                onClick={onToggleCollapse}
                aria-label="Minimizar pedido"
                title="Minimizar pedido"
              >
                ─
              </button>
            ) : null}
          </div>

          {!online ? (
            <p className="cart-offline-hint">
              Sin internet — al guardar se queda en la tablet y se envía después.
            </p>
          ) : null}

          <div className="cart-cliente">
            <SucursalPicker />
            {vendedor ? (
              <div className="cart-vendedor-readonly">
                <span>Vendedor</span>
                <strong>{vendedor.nombre}</strong>
              </div>
            ) : null}
            <textarea
              rows={2}
              className="cart-notas-generales"
              placeholder="Notas generales del pedido"
              value={cliente.notas}
              onChange={(e) => dispatch({ type: 'SET_CLIENTE', data: { notas: e.target.value } })}
            />
          </div>

          <div className="cart-scroll">
            <div className="cart-items">
              {!items.length ? (
                <p className="cart-empty">Toca un producto para verlo o usa + para agregar rápido</p>
              ) : (
                items.map((item) => (
                  <div key={item.lineId} className="cart-item">
                    {item.imagen ? (
                      <img src={imageUrl(item.imagen)} alt="" className="cart-item-img" />
                    ) : (
                      <div className="cart-item-img cart-item-img-empty" />
                    )}
                    <div className="cart-item-info">
                      <strong className="cart-item-code">
                        {/^REF\.\d+/i.test(String(item.observaciones || '').trim())
                          ? String(item.observaciones).trim()
                          : item.codigo}
                      </strong>
                      <span className="cart-item-name">{item.nombre}</span>
                      {/^REF\.\d+/i.test(String(item.observaciones || '').trim()) ? (
                        <em className="cart-item-sku">Producto {item.codigo}</em>
                      ) : null}
                      <span className="cart-item-price">{formatCOP(item.precio_unitario)}</span>
                      <CartItemObsField
                        lineId={item.lineId}
                        value={item.observaciones || ''}
                        onCommit={(observaciones) =>
                          dispatch({
                            type: 'SET_OBS',
                            lineId: item.lineId,
                            observaciones,
                          })
                        }
                      />
                    </div>
                      <CartItemQtyField
                        lineId={item.lineId}
                        value={item.cantidad}
                        onCommit={(qty) =>
                          dispatch({ type: 'SET_QTY', lineId: item.lineId, qty })
                        }
                      />
                  </div>
                ))
              )}
            </div>
          </div>

          {items.length > 0 ? (
            <div className="cart-opciones-sticky">
              <PedidoOpciones collapsible />
            </div>
          ) : null}

          <div className="cart-footer">
            <div className="cart-total">
              <span>Total</span>
              <strong>{formatCOP(subtotal)}</strong>
            </div>
            {error ? <p className="cart-error">{error}</p> : null}
            <button
              type="button"
              className="btn-primary btn-lg"
              disabled={sending || !items.length}
              onClick={() => {
                setError('');
                setShowConfirm(true);
              }}
            >
              {online ? 'Revisar pedido' : 'Revisar (sin internet)'}
            </button>
            {items.length > 0 ? (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  if (cliente.visita_id && !cliente.visita_fin) {
                    cerrarVisitaSilent(cliente.visita_id);
                  }
                  dispatch({ type: 'CLEAR' });
                }}
              >
                Vaciar carrito
              </button>
            ) : null}
          </div>

          <OrderConfirmModal
            open={showConfirm || Boolean(successSnap)}
            onClose={() => {
              if (successSnap) finishSuccess();
              else setShowConfirm(false);
            }}
            onGuardarLocal={guardarLocal}
            onEnviarOficina={enviarPedidoOficina}
            onDone={finishSuccess}
            sending={sending}
            offline={!online}
            error={error}
            success={successSnap}
          />
        </>
      )}
    </aside>
  );
}
